import { useEffect, useRef } from 'react';

// Minimal local types — don't depend on lib.dom shipping the Wake Lock API.
type WakeLockSentinelLike = {
  release: () => Promise<void>;
  addEventListener: (type: 'release', listener: () => void) => void;
};
type WakeLockLike = { request: (type: 'screen') => Promise<WakeLockSentinelLike> };

/**
 * Keep the screen awake while `active` is true (e.g. during an in-flight trip).
 *
 * The Screen Wake Lock API auto-releases when the tab is hidden, so we re-acquire
 * on `visibilitychange` → visible. Feature-detected and best-effort: unsupported
 * browsers no-op, and a rejected `request()` (not visible, low battery) is
 * swallowed so it never throws into render. Needs a secure context (https).
 */
export function useWakeLock(active: boolean): void {
  const sentinelRef = useRef<WakeLockSentinelLike | null>(null);

  useEffect(() => {
    if (!active) return;
    const nav = navigator as Navigator & { wakeLock?: WakeLockLike };
    if (!nav.wakeLock) return; // unsupported → no-op
    const wakeLock = nav.wakeLock;

    let cancelled = false;

    const acquire = async () => {
      if (sentinelRef.current) return; // already held
      try {
        const sentinel = await wakeLock.request('screen');
        if (cancelled) {
          void sentinel.release();
          return;
        }
        sentinelRef.current = sentinel;
        // The OS releases the lock when the tab is hidden — drop our ref so the
        // visibilitychange handler re-acquires on return to the foreground.
        sentinel.addEventListener('release', () => {
          sentinelRef.current = null;
        });
      } catch {
        // best-effort: ignore (not visible, low battery, permission denied)
      }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') void acquire();
    };

    void acquire();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      const sentinel = sentinelRef.current;
      sentinelRef.current = null;
      if (sentinel) void sentinel.release();
    };
  }, [active]);
}
